import axios from 'axios';
import {
	JSONRPCClient,
	JSONRPCServer,
	JSONRPCServerAndClient,
	TypedJSONRPCClient,
	TypedJSONRPCServer,
	TypedJSONRPCServerAndClient
} from "json-rpc-2.0";
import {
	AIAPIMethods,
	AICallbackMethods,
	UpdateType
} from "common";

import {ExecutableFunction} from "./functions/ExecutableFunction";
import config from '../../config';
import {FeatureFlagNames} from "../../interfaces/enums";
import SessionUtils from '../utilities/SessionUtils';

export const WEB_SOCKET_DISCONNECT_EVENT = "WebSocketDisconnect";
export const WEB_SOCKET_CONNECT_EVENT = "WebSocketConnect";

export class WebSocketConnectionManager
{
	private static instance: WebSocketConnectionManager;
	jsonRpcClient: TypedJSONRPCClient<AIAPIMethods>;
	private jsonRpcServer: TypedJSONRPCServer<AICallbackMethods>;
	private serverAndClient: TypedJSONRPCServerAndClient<AICallbackMethods, AIAPIMethods>;
	callbacks: Array<(type: UpdateType, message: string, guid: string, total: number, completed: number, sessionId: string) => void> = [];
	private retryCount = 0;
	private maxRetries = 20;
	connected = false;
	private ws: WebSocket | null = null;
	private reconnectTimeout: NodeJS.Timeout | null = null;
	private heartbeatInterval: NodeJS.Timeout | null = null;
	private connectionStartTime: number = 0;

	private constructor()
	{
		this.jsonRpcServer = new JSONRPCServer();
		this.jsonRpcClient = new JSONRPCClient((jsonRPCRequest) => this.handleJsonRpcRequest(jsonRPCRequest));
		this.serverAndClient = new JSONRPCServerAndClient(this.jsonRpcServer, this.jsonRpcClient);
		this.initializeServerAndClientMethods();
	}

	public static getInstance(): WebSocketConnectionManager
	{
		if (!WebSocketConnectionManager.instance)
		{
			WebSocketConnectionManager.instance = new WebSocketConnectionManager();
		}
		return WebSocketConnectionManager.instance;
	}

	private async handleJsonRpcRequest(jsonRPCRequest)
	{
		fetch(`${config.AI_SERVER_ADDRESS}/json-rpc`, {
			method: "POST",
			headers: {
				"content-type": "application/json",
				"Authorization": axios.defaults.headers.common['Authorization'] as string
			},
			body: JSON.stringify(jsonRPCRequest),
		}).then(async (response) =>
		{
			if (response.status === 200)
			{
				const jsonRPCResponse = await response.json();
				return this.jsonRpcClient.receive(jsonRPCResponse);
			}
			else if (jsonRPCRequest.id !== undefined)
			{
				throw new Error(response.statusText);
			}
		}).catch((e) =>
		{
			console.warn(`Failed to fetch ${config.AI_SERVER_ADDRESS}/json-rpc}`);
		});
	}

	private initializeServerAndClientMethods()
	{
		this.serverAndClient.addMethod("updateStatus", ({type, message, guid, completed, total, sessionId}) =>
		{
			// Call all registered callbacks
			try
			{
				this.callbacks.forEach(callback =>
				{
					try
					{
						callback(type, message, guid, completed, total, sessionId)
					}
					catch (e)
					{
						console.error(e);
					}
				});
			}
			catch (e)
			{
				console.error(e);
			}
		});

		this.serverAndClient.addMethod("callAPIFunction", async ({
			functionName,
			parameters,
			messageGuid,
			description,
			sessionId
		}, serverParams) =>
		{
			const functionToExecute = this.getSecondaryPrompts().find((value) => value.name === functionName);

			if (functionToExecute)
			{
				// Notify the UI
				this.callbacks.forEach(callback =>
				{
					callback("functionCall", description, messageGuid, 1, 1, sessionId);
				});

				// Call the tool. It might return a value or a promise.
				const out = functionToExecute.execute(parameters, messageGuid, sessionId);

				// Ensure we always return a promise.
				return out instanceof Promise ? await out : out;
			}
			else
			{
				console.warn(`Function "${functionName}" not found. Unable to execute.`);
			}
		});

		// Note: Heartbeat is handled via raw WebSocket messages, not JSON-RPC methods
	}

	private secondaryPrompts: Array<ExecutableFunction> = [];

	public setSecondaryPrompts(secondaryPrompts: Array<ExecutableFunction>)
	{
		this.secondaryPrompts = secondaryPrompts;
	}

	private getSecondaryPrompts(): Array<ExecutableFunction>
	{
		return this.secondaryPrompts;
	}

	private cleanupConnection()
	{
		if (this.reconnectTimeout)
		{
			clearTimeout(this.reconnectTimeout);
			this.reconnectTimeout = null;
		}
		if (this.heartbeatInterval)
		{
			clearInterval(this.heartbeatInterval);
			this.heartbeatInterval = null;
		}
		if (this.ws)
		{
			try
			{
				this.ws.close();
			}
			catch (error)
			{
				console.warn("Error closing WebSocket:", error);
			}
			this.ws = null;
		}
		this.connected = false;
	}

	private startHeartbeat()
	{
		if (this.heartbeatInterval)
		{
			clearInterval(this.heartbeatInterval);
		}

		this.heartbeatInterval = setInterval(() =>
		{
			if (this.ws && this.ws.readyState === WebSocket.OPEN && this.connected)
			{
				try
				{
					// Send a native WebSocket ping frame instead of JSON-RPC ping
					// Note: Browser WebSocket API doesn't expose ping() method directly
					// So we'll use a simple heartbeat message that the server can ignore
					this.ws.send(JSON.stringify({
						type: "heartbeat",
						timestamp: Date.now()
					}));
				}
				catch (error)
				{
					console.warn("Failed to send heartbeat:", error);
					this.handleConnectionFailure("Heartbeat failed");
				}
			}
		}, 30000); // Send heartbeat every 30 seconds
	}

	private handleConnectionFailure(reason: string)
	{
		console.warn(`WebSocket connection failed: ${reason}`);
		this.connected = false;
		this.cleanupConnection();
		
		// Notify UI of disconnection
		document.dispatchEvent(new CustomEvent("WebSocketDisconnect"));
		
		// Attempt reconnection with exponential backoff
		this.scheduleReconnection();
	}

	private scheduleReconnection()
	{
		if (this.reconnectTimeout)
		{
			clearTimeout(this.reconnectTimeout);
		}

		// Increment retry count
		this.retryCount++;

		// Check if we've exceeded max retries
		if (this.retryCount > this.maxRetries)
		{
			console.error(`Maximum reconnection attempts (${this.maxRetries}) exceeded. Stopping reconnection attempts.`);
			document.dispatchEvent(new CustomEvent("WebSocketDisconnect"));
			return;
		}

		const baseDelay = 1000; // 1 second base delay
		const maxDelay = 30000; // 30 seconds max delay
		const exponentialDelay = Math.min(baseDelay * Math.pow(2, this.retryCount - 1), maxDelay);
		
		console.log(`Scheduling reconnection attempt ${this.retryCount}/${this.maxRetries} in ${exponentialDelay}ms`);
		
		this.reconnectTimeout = setTimeout(() =>
		{
			this.establishConnection(false); // Skip cleanup since handleConnectionFailure already cleaned up
		}, exponentialDelay);
	}

	/**
	 * Establish a WebSocket connection to the AI server
	 * @param cleanup - Whether to cleanup existing connection first. 
	 *                  Set to false when reconnecting after handleConnectionFailure already cleaned up.
	 */
	public async establishConnection(cleanup: boolean = true)
	{
		if (SessionUtils.isFeatureFlagEnabled(FeatureFlagNames.aiAuthoring))
		{
			// Clean up any existing connection only if requested
			if (cleanup) 
			{
				this.cleanupConnection();
			}

			let authHeader = axios.defaults.headers.common['Authorization'];
			if (typeof authHeader !== "string")
			{
				console.log("Authorization header not available, retrying in 1 second...");
				setTimeout(() => this.establishConnection(false), 1000); // No cleanup needed for auth retry
				return;
			}

			const address = config.AI_SERVER_ADDRESS;
			if (!address)
			{
				throw new Error("AI_SERVER_ADDRESS not set");
			}
			const url = this.getUrl(address);

			authHeader = authHeader.replace('Bearer ', '');
			
			try
			{
				this.ws = new WebSocket(url, ["Authorization", authHeader]);
				this.connectionStartTime = Date.now();
				this.connected = false; // Reset connection state

				this.ws.addEventListener('open', () =>
				{
					try
					{
						console.log("WebSocket connection established successfully");
						document.dispatchEvent(new CustomEvent("WebSocketConnect"));
						this.retryCount = 0;
						this.connected = true;
						this.startHeartbeat();
					}
					catch (error)
					{
						console.error("Error in WebSocket open handler:", error);
						this.handleConnectionFailure("Error in open handler");
					}
				});

				this.ws.addEventListener('message', async (data) =>
				{
					try
					{
						const parsedData = JSON.parse(data.data.toString());
						
						// Handle heartbeat response (server might echo back or ignore)
						if (parsedData.type === "heartbeat")
						{
							this.connectionStartTime = Date.now();
							return;
						}
						
						await this.serverAndClient.receiveAndSend(parsedData);
					}
					catch (error)
					{
						console.error("Error processing WebSocket message:", error);
						// Don't fail the connection for message processing errors
					}
				});

				this.ws.addEventListener('close', (event) =>
				{
					try
					{
						console.log(`WebSocket connection closed with code: ${event.code}, reason: ${event.reason}`);
						this.handleConnectionFailure(`Connection closed (code: ${event.code})`);
					}
					catch (error)
					{
						console.error("Error in WebSocket close handler:", error);
					}
				});

				this.ws.addEventListener('error', (error) =>
				{
					try
					{
						console.error("WebSocket error:", error);
						this.handleConnectionFailure("WebSocket error occurred");
					}
					catch (error)
					{
						console.error("Error in WebSocket error handler:", error);
					}
				});

				// Set up the JSON-RPC client send method
				this.jsonRpcClient.send = async (payload, clientParams) =>
				{
					try
					{
						if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
						{
							throw new Error('WebSocket is not open');
						}

						this.ws.send(JSON.stringify(payload));
					}
					catch (e)
					{
						console.error('Failed to send WebSocket message:', e);
						throw e;
					}
				};

			}
			catch (error)
			{
				console.error("Failed to create WebSocket connection:", error);
				this.handleConnectionFailure("Failed to create connection");
			}
		}
	}

	private getUrl(address: string)
	{
		return address.replace(/^https?:\/\//, (match) => match === 'https://' ? 'wss://' : 'ws://');
	}

	public registerForUpdate(callback: (type: UpdateType, message: string, guid: string, total: number, completed: number) => void)
	{
		this.callbacks.push(callback);
	}

	/**
	 * Manually reset the connection state and attempt to reconnect
	 * Useful for recovery from stuck states
	 */
	public async resetConnection()
	{
		console.log("Manually resetting WebSocket connection...");
		this.retryCount = 0;
		// Cleanup and establish new connection
		await this.establishConnection(true); // Explicit cleanup for manual reset
	}

	/**
	 * Get the current connection status
	 */
	public getConnectionStatus()
	{
		return {
			connected: this.connected,
			retryCount: this.retryCount,
			maxRetries: this.maxRetries,
			wsReadyState: this.ws?.readyState
		};
	}

	/**
	 * Check if the connection is ready for sending messages
	 */
	public isReadyForSending(): boolean
	{
		return this.connected && this.ws?.readyState === WebSocket.OPEN;
	}

	// Additional methods related to JSON-RPC client/server interaction can be added here
} 